/* ============================================================================
   FileDrop.tsx — drop zone + input file + preview thumbnail.
   Drag & drop natif HTML5, pas de lib externe.
   ============================================================================ */

import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';

interface FileDropProps {
  accept: string;
  onFile: (file: File) => void;
  /** Si fournie : affiche le thumbnail au lieu de la drop zone vide */
  currentSrc?: string | null;
  /** Callback retirer (visible uniquement si currentSrc) */
  onClear?: () => void;
  /** Texte d'invite affiché en l'absence de fichier */
  hint?: string;
}

export function FileDrop({
  accept,
  onFile,
  currentSrc,
  onClear,
  hint = 'glisser une image ou cliquer',
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    // Reset pour permettre de re-uploader le même fichier
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) onFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className="controls-filedrop-wrap">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="controls-filedrop__input"
        onChange={handleChange}
        aria-label="Charger une image"
        data-testid="image-upload-input"
      />
      {currentSrc ? (
        <div className="controls-filedrop__preview">
          <div className="controls-filedrop__thumb" aria-label="Aperçu de l'image chargée">
            <img src={currentSrc} alt="aperçu" />
            <span className="controls-filedrop__halftone" aria-hidden="true" />
          </div>
          <div className="controls-filedrop__actions">
            <button
              type="button"
              className="controls-textbtn"
              onClick={openPicker}
              title="Remplacer l'image"
            >
              remplacer
            </button>
            {onClear ? (
              <button
                type="button"
                className="controls-textbtn controls-textbtn--danger"
                onClick={onClear}
                title="Retirer l'image"
                aria-label="Retirer l'image"
              >
                × retirer
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`controls-filedrop${dragOver ? ' controls-filedrop--over' : ''}`}
          onClick={openPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          aria-label="Charger une image"
        >
          <span className="controls-filedrop__icon" aria-hidden="true">↓</span>
          <span className="controls-filedrop__hint">{hint}</span>
        </button>
      )}
    </div>
  );
}
